import { CompleteSalarySnapshot } from '@/types/salarySnapshot'
import { getCompensationCategoryInfo } from '@/types/compensation'
import { TipsHandlingMode } from '@/types'

/**
 * Normalize feeOnTips to TipsHandlingMode (handles backward compatibility with boolean)
 */
function normalizeFeeOnTips(value?: TipsHandlingMode | boolean): TipsHandlingMode {
  if (typeof value === 'boolean') {
    return value ? 'fleet_commission' : 'driver_share'
  }
  return value || 'fleet_commission'
}

function formatCurrency(amount: number): string {
  return amount.toLocaleString('sv-SE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
}

function formatPlatformName(platform: string): string {
  const names: Record<string, string> = {
    uber: 'Uber',
    bolt: 'Bolt',
    fair: 'Fair',
    hack_rides: 'Hack Rides',
    'friåkning': 'Friåkning',
    other: 'Annat'
  }
  return names[platform.toLowerCase()] || platform
}

interface ReviewExpandedContentProps {
  snapshot: CompleteSalarySnapshot
}

export function ReviewExpandedContent({ snapshot }: ReviewExpandedContentProps) {
  const { rawEarnings, totals, compensations, fleetSettings } = snapshot

  // Group raw earnings by platform
  const earningsByPlatform = rawEarnings.reduce((acc, earning) => {
    const platform = earning.platform || 'unknown'
    if (!acc[platform]) {
      acc[platform] = { gross: 0, net: 0, tips: 0 }
    }
    acc[platform].gross += earning.grossEarnings
    acc[platform].net += earning.netEarnings
    acc[platform].tips += earning.tips
    return acc
  }, {} as Record<string, { gross: number; net: number; tips: number }>)

  const activeCompensations = compensations.filter(c => c.type === 'compensation')
  const activeDeductions = compensations.filter(c => c.type === 'deduction')

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left column: Earnings and compensations */}
      <div className="space-y-4">
        {/* Earnings by platform */}
        <div>
          <div className="text-sm font-medium mb-2">Intäkter per plattform</div>
          <div className="space-y-1">
            {Object.entries(earningsByPlatform).map(([platform, data]) => (
              <div key={platform} className="flex justify-between text-sm">
                <span>{formatPlatformName(platform)}</span>
                <span>
                  <span className="text-muted-foreground">Netto:</span>{' '}
                  <span className="font-medium">{formatCurrency(data.net)} kr</span>
                  {data.tips > 0 && (
                    <span className="text-muted-foreground ml-2">
                      (dricks: {formatCurrency(data.tips)} kr)
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Compensations */}
        {(activeCompensations.length > 0 || activeDeductions.length > 0) && (
          <div>
            <div className="text-sm font-medium mb-2">Ersättningar & Avdrag</div>
            <div className="space-y-1">
              {activeCompensations.map(comp => {
                const categoryInfo = getCompensationCategoryInfo(comp.category)
                return (
                  <div key={comp.id} className="flex justify-between text-sm">
                    <span>
                      {categoryInfo.label}
                      {comp.comment && (
                        <span className="text-muted-foreground text-xs ml-1">({comp.comment})</span>
                      )}
                    </span>
                    <span className="text-green-700 font-medium">+{formatCurrency(comp.amount)} kr</span>
                  </div>
                )
              })}
              {activeDeductions.map(comp => {
                const categoryInfo = getCompensationCategoryInfo(comp.category)
                return (
                  <div key={comp.id} className="flex justify-between text-sm">
                    <span>
                      {categoryInfo.label}
                      {comp.comment && (
                        <span className="text-muted-foreground text-xs ml-1">({comp.comment})</span>
                      )}
                    </span>
                    <span className="text-red-700 font-medium">-{formatCurrency(comp.amount)} kr</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {/* Right column: Salary calculation */}
      <div>
        <div className="text-sm font-medium mb-2">Löneberäkning</div>
        <div className="text-sm space-y-1">
          {(() => {
            const tipsMode = normalizeFeeOnTips(fleetSettings.feeOnTips)
            const tipsExcludedFromShareable = tipsMode !== 'fleet_commission'

            return (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Nettointäkter</span>
                  <span>{formatCurrency(totals.netEarnings)} kr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">- Moms ({(fleetSettings.vatRate * 100).toFixed(0)}%)</span>
                  <span>-{formatCurrency(totals.vatAmount)} kr</span>
                </div>
                {tipsExcludedFromShareable && totals.tips > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">- Dricks</span>
                    <span>-{formatCurrency(totals.tips)} kr</span>
                  </div>
                )}
                <div className="flex justify-between font-medium">
                  <span>= Delbar summa</span>
                  <span>{formatCurrency(totals.shareableAmount)} kr</span>
                </div>
              </>
            )
          })()}

          <div className="border-t my-2" />

          <div className="flex justify-between">
            <span className="text-muted-foreground">× Förarandel ({totals.appliedDriverRevenueShare.toFixed(0)}%)</span>
            <span>{formatCurrency(totals.afterFleetCommission)} kr</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">+ Semesterersättning ({(fleetSettings.vacationPayRate * 100).toFixed(0)}%)</span>
            <span>+{formatCurrency(totals.vacationPay)} kr</span>
          </div>

          <div className="border-t my-2" />

          <div className="flex justify-between font-medium">
            <span>Bruttolön</span>
            <span>{formatCurrency(totals.finalGrossSalary)} kr</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">- Skatt ({totals.effectiveTaxRate.toFixed(1)}%)</span>
            <span>-{formatCurrency(totals.taxAmount)} kr</span>
          </div>

          <div className="border-t my-2" />

          <div className="flex justify-between font-medium">
            <span>Lön efter skatt</span>
            <span>{formatCurrency(totals.finalNetSalary)} kr</span>
          </div>
          {totals.cashReceived > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">- Kontant förskott</span>
              <span className="text-red-700">-{formatCurrency(totals.cashReceived)} kr</span>
            </div>
          )}

          <div className="border-t-2 my-2" />

          <div className="flex justify-between font-semibold">
            <span>Att utbetala</span>
            <span className="text-green-700">{formatCurrency(totals.amountToPay - (totals.untaxedTips || 0))} kr</span>
          </div>
          {totals.untaxedTips > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">+ Dricks (oskattad)</span>
              <span className="text-green-700">+{formatCurrency(totals.untaxedTips)} kr</span>
            </div>
          )}

          <div className="flex justify-between text-xs text-muted-foreground pt-2 border-t mt-2">
            <span>Arbetsgivarkostnad</span>
            <span>{formatCurrency(totals.totalEmployerCost)} kr</span>
          </div>
        </div>
      </div>
    </div>
  )
}
